import { c as _c } from "react/compiler-runtime";
import { SubscriptionStatusEnum } from 'modules/payment/subscriptions/enums/subscription-status-enum';
import SettingsLayout from 'modules/profile/components/settings-layout';
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys';
import { MANAGE_SUBSCRIPTIONS_API } from 'modules/profile/manage-subscriptions/api/cancel-subscriptions-api';
import CancelSubscriptionModal from 'modules/profile/manage-subscriptions/components/cancel-subscription-modal';
import UpdateSubscriptionFieldsModal from 'modules/profile/manage-subscriptions/components/update-subscription-fields-modal';
import { ManageSubscriptionsLoco } from 'modules/profile/manage-subscriptions/constants/manage-subscriptions-loco';
import { SubscriptionStatusUiEnum } from 'modules/profile/manage-subscriptions/enums/subscription-status-ui';
import { useManageSubscriptions } from 'modules/profile/manage-subscriptions/hooks/use-manage-subscriptions';
import { ManageSubscriptionsInterface } from 'modules/profile/manage-subscriptions/types/manage-subscriptions-interface';
import { getSubscriptionStatus } from 'modules/profile/manage-subscriptions/utils/get-subscription-status';
import { NextPage } from 'next';
import React from 'react';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useUpdateData } from 'shared/hooks/use-update-data';
import useUser from 'shared/hooks/use-user';
import { usePagination } from 'shared/route-query/hooks/use-pagination';
import { dateTimeFormat } from 'shared/utils/date-time-format';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
import { CheckCircleIcon, CloseCircleIcon, StatusCanceling } from '@systemeio/ui/icons';
import { Tooltip } from '@systemeio/ui';
import { PricePlanTooltip } from 'shared/components/tooltip/price-plan-tooltip';
const getStatusIcon = (subscription: ManageSubscriptionsInterface) => {
  const status = getSubscriptionStatus(subscription);
  if (status === SubscriptionStatusUiEnum.active || status === SubscriptionStatusUiEnum.completed) {
    return <CheckCircleIcon className={`fill-green h-[16px] w-[16px] sm:h-[20px] sm:w-[20px]`} />;
  }
  if (status === SubscriptionStatusUiEnum.cancelled) {
    return <CloseCircleIcon className={`fill-danger h-[16px] w-[16px] sm:h-[20px] sm:w-[20px]`} />;
  }
  if (status === SubscriptionStatusUiEnum.canceling) {
    return <StatusCanceling className={`h-[16px] w-[16px] sm:h-[20px] sm:w-[20px]`} />;
  }
};
const ManageSubscriptions: NextPage = () => {
  const $ = _c(71);
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const {
    setPagination,
    pagination,
    setLimit,
    limit
  } = usePagination();
  let t0;
  if ($[0] !== t || $[1] !== user?.dateFormat || $[2] !== user?.timeFormat || $[3] !== user?.timezone) {
    t0 = subscription => {
      const status = getSubscriptionStatus(subscription);
      if (status === SubscriptionStatusUiEnum.active) {
        return t("global.active");
      }
      if (status === SubscriptionStatusUiEnum.cancelled) {
        const deactivatedAt = subscription.subscriptionItems[0].deactivatedAt;
        if (deactivatedAt !== null) {
          return `${t("global.cancelled")} - ${dateTimeFormat(deactivatedAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone
          })}`;
        }
        return t("global.cancelled");
      }
      if (status === SubscriptionStatusUiEnum.canceling) {
        return t("global.canceling");
      }
      if (status === SubscriptionStatusUiEnum.completed) {
        return t("dashboard.subscriptions.subscription_completed");
      }
    };
    $[0] = t;
    $[1] = user?.dateFormat;
    $[2] = user?.timeFormat;
    $[3] = user?.timezone;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const getStatusLabel = t0;
  let t1;
  if ($[5] !== t) {
    t1 = t("dashboard.subscriptions.title_plural");
    $[5] = t;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = {
      key: "subscriptionItems",
      header: t1,
      widthPercent: 30,
      onRender: _temp
    };
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t) {
    t3 = t("global.status");
    $[9] = t;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== getStatusLabel) {
    t4 = subscription_1 => <div className="flex items-center lg:ml-4"><Tooltip label={getStatusLabel(subscription_1)} mode="hover" key="remove-tooltip">{getStatusIcon(subscription_1)}</Tooltip></div>;
    $[11] = getStatusLabel;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = {
      key: "status",
      header: t3,
      widthPercent: 40,
      onRender: t4
    };
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t) {
    t6 = t("dashboard.subscriptions.date");
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== user?.dateFormat || $[19] !== user?.timeFormat || $[20] !== user?.timezone) {
    t7 = subscription_2 => <span>{dateTimeFormat(subscription_2.createdAt, {
        timeFormat: user?.timeFormat,
        dateFormat: user?.dateFormat,
        timeZone: user?.timezone
      })}</span>;
    $[18] = user?.dateFormat;
    $[19] = user?.timeFormat;
    $[20] = user?.timezone;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t6 || $[23] !== t7) {
    t8 = {
      key: "createdAt",
      header: t6,
      widthPercent: 30,
      onRender: t7
    };
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t2 || $[26] !== t5 || $[27] !== t8) {
    t9 = [t2, t5, t8];
    $[25] = t2;
    $[26] = t5;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const manageSubscriptionsColumns = t9;
  let t10;
  if ($[29] !== limit || $[30] !== pagination) {
    t10 = {
      limit,
      pagination
    };
    $[29] = limit;
    $[30] = pagination;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const {
    data: subscriptions,
    mutate
  } = useManageSubscriptions(t10);
  let t11;
  if ($[32] !== setPagination) {
    t11 = {
      setPagination,
      api: MANAGE_SUBSCRIPTIONS_API
    };
    $[32] = setPagination;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const updateData = useUpdateData(t11);
  let t12;
  if ($[34] !== mutate || $[35] !== subscriptions || $[36] !== updateData) {
    t12 = async subscriptionItemId => {
      await updateData(false, undefined, {
        mutationData: subscriptions,
        onMutate: async () => {
          await mutate(data => {
            if (data) {
              return {
                items: data.items.map(item => {
                  const subscriptionItem = item.subscriptionItems.find(el => el.id === subscriptionItemId);
                  if (subscriptionItem) {
                    item = {
                      ...item,
                      subscriptionItems: item.subscriptionItems.map(el_0 => {
                        if (el_0 === subscriptionItem) {
                          return {
                            ...el_0,
                            status: SubscriptionStatusEnum.cancelled
                          };
                        } else {
                          return el_0;
                        }
                      })
                    };
                  }
                  return item;
                }),
                hasMore: data.hasMore
              };
            }
          }, false);
        }
      });
    };
    $[34] = mutate;
    $[35] = subscriptions;
    $[36] = updateData;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const onUnsubscribe = t12;
  let t13;
  if ($[38] !== t) {
    t13 = t(settingsMenuLocoKeys.manage_subscriptions);
    $[38] = t;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t13) {
    t14 = [{
      caption: t13,
      isStatic: true
    }];
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t) {
    t15 = t("dashboard.settings.manage_subscriptions.cancel_subscription");
    $[42] = t;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== onUnsubscribe) {
    t16 = (opened, onClose, subscription_4, key) => <CancelSubscriptionModal key={key} subscription={subscription_4} opened={opened} onClose={onClose} onUnsubscribe={onUnsubscribe} />;
    $[44] = onUnsubscribe;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t15 || $[47] !== t16) {
    t17 = {
      name: "cancel-subscription",
      shouldRender: _temp2,
      caption: t15,
      onActionRender: t16
    };
    $[46] = t15;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== t) {
    t18 = t("dashboard.settings.manage_subscriptions.update_information");
    $[49] = t;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== t18) {
    t19 = {
      name: "update-information",
      shouldRender: _temp3,
      caption: t18,
      onActionRender: _temp4
    };
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== t17 || $[54] !== t19) {
    t20 = {
      custom: [t17, t19]
    };
    $[53] = t17;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== limit || $[57] !== setLimit) {
    t21 = {
      limit,
      setLimit
    };
    $[56] = limit;
    $[57] = setLimit;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== pagination || $[60] !== setPagination) {
    t22 = {
      pagination,
      setPagination
    };
    $[59] = pagination;
    $[60] = setPagination;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== manageSubscriptionsColumns || $[63] !== subscriptions || $[64] !== t20 || $[65] !== t21 || $[66] !== t22) {
    t23 = <Table actions={t20} data={subscriptions} columns={manageSubscriptionsColumns} limiter={t21} paginator={t22} />;
    $[62] = manageSubscriptionsColumns;
    $[63] = subscriptions;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== t14 || $[69] !== t23) {
    t24 = <SettingsLayout needSave={false} titles={t14}>{t23}</SettingsLayout>;
    $[68] = t14;
    $[69] = t23;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  return t24;
};
export const getServerSideProps = getBaseServerSideProps(ManageSubscriptionsLoco, 'global.settings');
export default ManageSubscriptions;
function _temp(subscription_0) {
  return <PricePlanTooltip pricePlan={subscription_0.subscriptionItems[0].orderItem.pricePlan} hiddenInfo={true}><span className="cursor-pointer">{subscription_0.subscriptionItems[0].orderItem.pricePlan.name}</span></PricePlanTooltip>;
}
function _temp2(subscription_3) {
  return getSubscriptionStatus(subscription_3) === SubscriptionStatusUiEnum.active;
}
function _temp3(subscription_5) {
  return getSubscriptionStatus(subscription_5) === SubscriptionStatusUiEnum.active;
}
function _temp4(opened_0, onClose_0, subscription_6, key_0) {
  return <UpdateSubscriptionFieldsModal opened={opened_0} onClose={onClose_0} subscriptionId={subscription_6.subscriptionItems[0].id} key={key_0} />;
}